import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Group, Button, Badge, FileButton } from '@mantine/core'
import { IconPhoto } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'

/**
 * ช่องเซ็นชื่อ — วาดด้วยนิ้ว/เมาส์ หรือแนบรูปลายเซ็นแทนก็ได้ (ตั้งใจให้ทั้ง 2 ทางเลือกทำงานได้บนทุกอุปกรณ์
 * ไม่แยกลอจิกตามชนิดอุปกรณ์ — ให้ผู้ใช้เลือกเอง) ใช้ร่วมกันทั้งหน้า Handover (ต้อง Login) และหน้ายืนยันรับของ
 * แบบสาธารณะผ่าน magic link (ไม่ต้อง Login)
 */
export default function SignaturePad({ onSave, label, value, width = 380, height = 130, hint }: { onSave: (data: string) => void; label: string; value?: string; width?: number; height?: number; hint?: string }) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const fileResetRef = useRef<() => void>(null)
  const [importedImg, setImportedImg] = useState<string>('')

  // Bezier smoothing — track last 2 points to draw midpoint curves
  const lastPt  = useRef<{ x: number; y: number } | null>(null)
  const prevPt  = useRef<{ x: number; y: number } | null>(null)
  const lastTime = useRef<number>(0)
  const lastWidth = useRef<number>(2.5)

  // Scale canvas to device pixel ratio for crisp lines on Retina/HiDPI screens
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const W = width, H = height
    canvas.width  = W * dpr
    canvas.height = H * dpr
    canvas.style.width  = `${W}px`
    canvas.style.height = `${H}px`
    const ctx = canvas.getContext('2d')!
    ctx.scale(dpr, dpr)

    // Restore existing value if any
    if (value) {
      const img = new window.Image()
      img.onload = () => {
        ctx.clearRect(0, 0, W, H)
        const scale = Math.min(W / img.width, H / img.height)
        const w = img.width * scale, h = img.height * scale
        ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h)
      }
      img.src = value
      setImportedImg(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getPos = (e: any, c: HTMLCanvasElement) => {
    const r = c.getBoundingClientRect()
    const clientX = e.touches?.[0]?.clientX ?? e.clientX
    const clientY = e.touches?.[0]?.clientY ?? e.clientY
    // CSS pixels (already scaled by dpr in canvas context)
    return { x: (clientX - r.left), y: (clientY - r.top) }
  }

  const start = (e: any) => {
    if (e.type === 'touchstart') e.preventDefault()
    drawing.current = true
    setImportedImg('')
    const c = canvasRef.current!
    const p = getPos(e, c)
    lastPt.current  = p
    prevPt.current  = null
    lastTime.current = Date.now()
    lastWidth.current = 2.5
    const ctx = c.getContext('2d')!
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
  }

  const draw = (e: any) => {
    if (!drawing.current) return
    if (e.type === 'touchmove') e.preventDefault()
    const c = canvasRef.current!
    const ctx = c.getContext('2d')!
    const now = Date.now()
    const curr = getPos(e, c)
    const last = lastPt.current!

    // Velocity-based width: fast stroke → thin, slow stroke → thick
    const dist = Math.sqrt((curr.x - last.x) ** 2 + (curr.y - last.y) ** 2)
    const dt   = Math.max(1, now - lastTime.current)
    const vel  = dist / dt                        // px/ms
    const targetWidth = Math.max(1, Math.min(4, 3.5 - vel * 10))
    // Ease toward target width for smooth transitions
    const width = lastWidth.current * 0.85 + targetWidth * 0.15
    lastWidth.current = width

    // Quadratic bezier through midpoints — eliminates jagged line segments
    const mid = { x: (last.x + curr.x) / 2, y: (last.y + curr.y) / 2 }
    const prevMid = prevPt.current
      ? { x: (prevPt.current.x + last.x) / 2, y: (prevPt.current.y + last.y) / 2 }
      : last

    ctx.beginPath()
    ctx.moveTo(prevMid.x, prevMid.y)
    ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y)

    ctx.lineWidth   = width
    ctx.lineCap     = 'round'
    ctx.lineJoin    = 'round'
    ctx.strokeStyle = '#111111'
    ctx.stroke()

    prevPt.current  = last
    lastPt.current  = curr
    lastTime.current = now
  }

  const stop = () => {
    if (!drawing.current) return
    drawing.current = false
    // Draw final dot if barely moved (tap/click)
    if (lastPt.current && !prevPt.current) {
      const c = canvasRef.current!
      const ctx = c.getContext('2d')!
      ctx.beginPath()
      ctx.arc(lastPt.current.x, lastPt.current.y, 1.5, 0, Math.PI * 2)
      ctx.fillStyle = '#111111'
      ctx.fill()
    }
    prevPt.current = null
    lastPt.current = null
    onSave(canvasRef.current!.toDataURL('image/png'))
  }

  const clear = () => {
    const c = canvasRef.current!
    c.getContext('2d')!.clearRect(0, 0, 380, 130)
    setImportedImg('')
    fileResetRef.current?.()
    onSave('')
  }

  const handleImport = (file: File | null) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      notifications.show({ color: 'red', message: 'กรุณาเลือกไฟล์รูปภาพเท่านั้น' })
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      const c = canvasRef.current!
      const ctx = c.getContext('2d')!
      const W = width, H = height
      const img = new window.Image()
      img.onload = () => {
        ctx.clearRect(0, 0, W, H)
        const scale = Math.min(W / img.width, H / img.height)
        const w = img.width * scale, h = img.height * scale
        ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h)
        const merged = c.toDataURL('image/png')
        setImportedImg(merged)
        onSave(merged)
        fileResetRef.current?.()
        notifications.show({ color: 'teal', message: 'นำเข้าลายเซ็นเรียบร้อย' })
      }
      img.src = dataUrl
    }
    reader.readAsDataURL(file)
  }

  const showLabelRow = !!label || (hint !== undefined ? !!hint : true)

  return (
    <Box>
      {showLabelRow && (
        <Group justify="space-between" align="center" mb={4} style={{ minHeight: 24 }}>
          <Text size="sm" fw={500}>{label}</Text>
          {hint !== undefined ? (hint && <Text size="xs" c="dimmed">{hint}</Text>) : <Text size="xs" c="dimmed">{t('handover.autoSaved')}</Text>}
        </Group>
      )}
      <Box style={{ border: '1px solid #ebebeb', borderRadius: 8, overflow: 'hidden', background: '#fafafa', position: 'relative', width: `${width}px`, height: `${height}px` }}>
        <canvas ref={canvasRef}
          style={{ display: 'block', touchAction: 'none', cursor: 'crosshair', width: `${width}px`, height: `${height}px` }}
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
        />
        {importedImg && (
          <Badge size="xs" color="blue" variant="filled" style={{ position: 'absolute', top: 6, right: 6 }}>
            {t("config.imported","Imported")}
          </Badge>
        )}
      </Box>
      <Group gap="xs" mt={4}>
        <Button size="xs" variant="subtle" color="gray" onClick={clear}>{t('handover.clearSignature')}</Button>
        <FileButton onChange={handleImport} accept="image/*" resetRef={fileResetRef}>
          {(props) => (
            <Button size="xs" variant="subtle" color="teal" leftSection={<IconPhoto size={14} />} {...props}>
              {t('handover.importSignature')}
            </Button>
          )}
        </FileButton>
      </Group>
    </Box>
  )
}
